import {
  Package,
  RefreshCcw,
  CircleCheck,
  Truck,
  XCircle, 
  CheckCircle, 
  FilePlus2,
  PackageCheck,
  HelpCircle,
  Undo2,
  Ban,
  Copy,
  Wallet,
  Headset,
  Clock,
  Info,
  FileText,
} from "lucide-react"
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom"; 
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { BASE_URL } from '../../../apiConfig';
import DashboardSkeleton from "../../../cors/DashboardSkeleton";
import { toast } from 'sonner';
import SelectDate from "../../../cors/SelectDate";
import ToggleShowStats from "../../../cors/ToggleShowStats";


const DEFAULT_STATE = {
  total_profit: 0,
  total_revenue: 0,
  total_orders: 0,
  orders_status: {},
  delivered_percentage: 0,
};

const getStatusColor = (status) => {
switch (status) {
  case "nouveau":
    return "bg-blue-100 text-blue-700 border border-blue-200";
  case "confirme":
    return "bg-green-100 text-green-700 border border-green-200";
  case "suivi":
    return "bg-amber-100 text-amber-700 border border-amber-200";
  case "reporte":
    return "bg-yellow-100 text-yellow-700 border border-yellow-200";
  case "indelivery":
    return "bg-indigo-100 text-indigo-700 border border-indigo-200";
  case "livre":
    return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  case "retour":
    return "bg-orange-100 text-orange-700 border border-orange-200";
  case "refuse":
    return "bg-red-100 text-red-700 border border-red-200";
  case "annule":
    return "bg-gray-200 text-gray-700 border border-gray-300";
  case "duplicated":
    return "bg-pink-100 text-pink-700 border border-pink-200";
  default:
    return "bg-gray-100 text-gray-700 border border-gray-200";
}
};

const getStatusText = (status, t) => {
switch (status) {
  case "nouveau": return t('status.nouveau');
  case "confirme": return t('status.confirme');
  case "reporte": return t('status.reporte');
  case "suivi": return t('status.suivi');
  case "indelivery": return t('status.indelivery');
  case "livre": return t('status.livre');
  case "retour": return t('status.retour');
  case "refuse": return t('status.refuse');
  case "annule": return t('status.annule');
  case "duplicated": return t('status.duplicated');
  default: return status;
}
};

const getStatusIcon = (status) => {
const iconSize = 40;
switch (status) {
  case "nouveau":
    return <FilePlus2 size={iconSize} />;
  case "confirme":
    return <CheckCircle size={iconSize} />;
  case "suivi":
    return <Headset size={iconSize} />;
  case "reporte":
    return <HelpCircle size={iconSize} />;
  case "indelivery":
    return <Truck size={iconSize} />;
  case "livre":
    return <PackageCheck size={iconSize} />;
  case "retour":
    return <Undo2 size={iconSize} />;
  case "refuse":
    return <XCircle size={iconSize} />;
  case "annule":
    return <Ban size={iconSize} />;
  case "duplicated":
    return <Copy size={iconSize} />;
  default:
    return null;
}
}

export default function DashboardLivreur() {
  const { token } = useAuth();
  const { t } = useLanguage();

  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [showStatsValues, setShowStatsValues] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const currentRangeRef = useRef(null);
  function CardWave({ color }) {
    return (
      <svg
        className="absolute bottom-0 left-0 w-full h-10 opacity-70"
        viewBox="0 0 300 60"
        preserveAspectRatio="none"
      >
        <path
          d="M0,40 C60,10 90,55 150,35 C210,15 250,50 300,25 L300,60 L0,60 Z"
          fill={color}
        />
      </svg>
    );
  }

  function HeroIllustration() {
    return (
      <svg viewBox="0 0 220 160" className="w-44 h-32 md:w-56 md:h-40" aria-hidden="true">
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* soft ground shadow */}
        <ellipse cx="110" cy="148" rx="80" ry="8" fill="#0D9488" opacity="0.08" />

        {/* scattered dots, top right */}
        <circle cx="190" cy="18" r="3" fill="#99F0DE" />
        <circle cx="200" cy="30" r="2" fill="#99F0DE" />
        <circle cx="178" cy="8" r="2" fill="#99F0DE" />

        {/* tilted background card */}
        <g transform="rotate(-4 115 60)">
          <rect x="55" y="8" width="122" height="98" rx="20" fill="#CFF3E8" />
        </g>

        {/* main white panel */}
        <rect x="68" y="24" width="96" height="68" rx="12" fill="#FFFFFF" stroke="#E3F6F0" strokeWidth="1" />

        {/* window dots */}
        <circle cx="82" cy="37" r="3" fill="#F87171" />
        <circle cx="91" cy="37" r="3" fill="#FBBF24" />
        <circle cx="100" cy="37" r="3" fill="#34D399" />

        {/* header bars */}
        <rect x="80" y="48" width="30" height="5" rx="2.5" fill="#0F766E" />
        <rect x="80" y="57" width="18" height="4" rx="2" fill="#99F0DE" />

        {/* chart with filled area + highlighted point */}
        <path d="M78,86 L78,72 L90,60 L102,68 L116,48 L152,58 L152,86 Z" fill="url(#chartFill)" />
        <polyline
          points="78,72 90,60 102,68 116,48 152,58"
          fill="none"
          stroke="#14B8A6"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="116" cy="48" r="3.5" fill="#FFFFFF" stroke="#0D9488" strokeWidth="2.5" />

        {/* floating mini stat card */}
        <g>
          <rect x="8" y="72" width="66" height="46" rx="14" fill="#0D9488" />
          <rect x="18" y="86" width="26" height="6" rx="3" fill="#CFF3E8" />
          <rect x="18" y="97" width="40" height="5" rx="2.5" fill="#5EEAD4" />
          <path d="M50,84 L58,84 L58,100" fill="none" stroke="#5EEAD4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M46,96 L52,90 L58,94 L64,84" fill="none" stroke="#F5C87A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* coin stack */}
        <g>
          <ellipse cx="152" cy="132" rx="17" ry="6" fill="#C9862F" />
          <rect x="135" y="122" width="34" height="10" fill="#EDB35F" />
          <ellipse cx="152" cy="122" rx="17" ry="6" fill="#F5C87A" />
          <ellipse cx="152" cy="122" rx="10" ry="3.5" fill="#FBDFA6" />
        </g>
        <g>
          <ellipse cx="172" cy="120" rx="13" ry="5" fill="#C9862F" />
          <rect x="159" y="112" width="26" height="8" fill="#EDB35F" />
          <ellipse cx="172" cy="112" rx="13" ry="5" fill="#F5C87A" />
          <ellipse cx="172" cy="112" rx="7.5" ry="2.8" fill="#FBDFA6" />
        </g>
      </svg>
    );
  }

  const fetchStats = useCallback(async (range = null) => {
      if (!token) {
          toast.error(t('errors.auth_token_missing'));
          setLoading(false);
          setShowContent(true);
          return;
      }

      setLoading(true);
      setShowContent(false);

      try {
          const requestBody = range ? { range } : {};
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

          const response = await fetch(BASE_URL + '/api/dashboard-stats', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify(requestBody),
              signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
              const errorText = await response.text();
              console.error(`API Error: ${response.status} - ${errorText}`);
              throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
          }

          const result = await response.json();

          if (result.code === 200 && result.data) {
              setState(prev => ({ ...DEFAULT_STATE, ...result.data }));
              setTimeout(() => setShowContent(true), 100);
          } else {
              console.error("Unexpected API response structure:", result);
              toast.error(t('errors.unexpected_format'));
              setShowContent(true);
          }
      } catch (err) {
          if (err.name === 'AbortError') {
              toast.error(t('errors.request_timeout'));
          } else {
              console.error("Fetch error:", err);
              toast.error(err.message || t('errors.loading_stats'));
          }
          setShowContent(true);
      } finally {
          setLoading(false);
          setRefreshing(false);
      }
  }, [token]);

  const handleDateRangeChange = useCallback((range) => {
      currentRangeRef.current = range;
      fetchStats(range);
  }, [fetchStats]);

  const handleRefresh = useCallback(() => {
      if (loading || refreshing) return;
      setRefreshing(true);
      fetchStats(currentRangeRef.current);
  }, [fetchStats, loading, refreshing]);

  useEffect(() => {
      fetchStats();
  }, [fetchStats]);

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  const stats = useMemo(() => [
      {
          title: t('stats.total_orders'),
          value: formatCount(state.total_orders),
          initialValue: "0",
          icon: Package,
          color: "bg-blue-500",
      },
      {
          title: t('stats.delivery_rate'),
          value: `${Number(state.delivered_percentage || 0).toFixed(2)} %`,
          initialValue: "0.00 %",
          icon: CircleCheck,
          color: "bg-green-500",
      },
      {
          title: t('stats.pending_payments'),
          value: `${formatMoney(state.total_revenue)} MAD`,
          initialValue: "0.00 MAD",
          icon: Clock,
          color: "bg-yellow-500",
      },
      {
          title: t('stats.net_profit'),
          value: `${formatMoney(state.total_profit)} MAD`,
          initialValue: "0.00 MAD",
          icon: Wallet,
          color: "bg-teal-500",
      },
  ], [state, t]);

  return (
      <div className="min-h-screen">
          <div className="flex">
              <main className="flex-1 md:ml-0">
                  <div className="py-8">
                      <div className="mx-auto px-4 sm:px-6 px-5">
                          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                  <div className="flex items-center gap-3 mb-4">
                                      <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent">
                                          {t('dashboard.title')}
                                      </h1>
                                  </div>
                                  <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                                      {t('dashboard.subtitle_livreur')}
                                  </p>
                              </div>
                              <div className="flex max-xl:grid items-center gap-3 max-lg:mt-10">
                                  <SelectDate onDateRangeChange={handleDateRangeChange} />
                                  <button 
                                      className="flex items-center max-w-max gap-1 rounded-xl z-50 bg-white shadow-sm py-2.5 px-4 cursor-pointer text-xs hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      onClick={handleRefresh}
                                      disabled={loading || refreshing}
                                  >
                                      <RefreshCcw 
                                          className={`w-4 h-4 text-[var(--color-primary-500)] transition-transform duration-500 ease-in-out ${
                                              loading || refreshing ? 'animate-spin' : ''
                                          }`} 
                                      />
                                      <span className="dark:text-white">{loading || refreshing ? t('dashboard.refreshing') : t('dashboard.refresh')}</span>
                                  </button>
                              </div>
                          </div>
                          <div className="flex items-center mb-2 px-2 w-full justify-end">
                            <ToggleShowStats showStatsValues={showStatsValues} setShowStatsValues={setShowStatsValues} />
                          </div>

                          {loading ? (
                              <DashboardSkeleton />
                          ) : (
                            <>
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4 mb-10">
                            {stats.map((stat, index) => (
                              <div
                                key={stat.title}
                                className="group relative bg-white rounded-3xl border border-gray-100 transition-all duration-500 ease-out hover:-translate-y-1"
                              >
                                {/* Elegant border glow */}
                                <div className={`absolute inset-0 rounded-3xl ${stat.color} opacity-0 opacity-10 transition-opacity duration-700 blur-md`} />
                                
                                {/* Subtle top accent line with animation */}
                                <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0.5 ${stat.color} rounded-full group-hover:w-1/2 transition-all duration-700 ease-out`} />
                                
                                <div className="relative p-6">
                                  <div className="flex items-start gap-4">
                                    {/* Minimal icon container */}
                                    <div className={`flex-shrink-0 ${stat.color} p-2.5 rounded-2xl transition-all duration-500 shadow-md scale-105`}>
                                      <stat.icon className="h-5 w-5 text-white" />
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      {/* Clean title */}
                                      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                                        {stat.title}
                                      </p>
                                      
                                      {/* Bold value with clean typography */}
                                      <p className="mt-1.5 text-2xl font-light text-gray-900 tracking-tight">
                                        {showStatsValues ? (showContent ? stat.value : stat.initialValue) : '••••••'}
                                      </p>
                                      
                                      {/* Minimal change indicator */}
                                      <div className="flex items-center gap-2 mt-1.5">
                                        <div className={`flex items-center gap-0.5 ${parseFloat(stat.change) > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                          <span className="text-xs font-medium">{stat.change}</span>
                                          <span className="text-[10px] font-light">
                                            {parseFloat(stat.change) > 0 ? '↑' : '↓'}
                                          </span>
                                        </div>
                                        <span className="text-[10px] text-gray-300 font-light">21 vs last month</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Minimal progress bar */}
                                  <div className="mt-4 h-[2px] bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${stat.color} rounded-full transition-all duration-1000 ease-out`}
                                      style={{ 
                                        width: `${Math.floor(Math.random() * 40 + 60)}%`,
                                        transitionDelay: `${index * 150}ms`
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>   

                            <div>
                                <div>
                                <div className={`mb-10 grid grid-cols-5 max-lg:gap-2 max-lg:grid-cols-2 gap-5 transition-all duration-500 delay-500 ${
                                showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                              }`}>

                                  <Link
                                    to="/orders/tout"
                                    className={`group relative rounded-2xl overflow-hidden transition-all ${showContent ? "scale-100" : "scale-95"} duration-300 hover:scale-[1.02] hover:shadow-[0_20px_60px_-20px_rgba(16,185,129,0.3)]`}
                                  >
                                    <div className="absolute inset-0  bg-emerald-300/40  rounded-2xl" />
                                    <div className="absolute inset-0 backdrop-blur-2xl bg-white/30 dark:bg dark:bg-stone-800/60 rounded-2xl" />
                                    <div className="absolute inset-0 rounded-2xl border border-white/50 dark:border-stone-700 group-hover:border-white/80 transition-colors duration-300" />
                                    <div className="absolute -top-20 -right-20 w-48 h-48 bg-emerald-400/20 dark:bg-stone-800/50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                                    <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-300/10 dark:bg-stone-800/30 rounded-full blur-3xl" />
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                    
                                    <div className="relative p-4 max-md:p-2.5">
                                      <div className="flex items-start justify-between">
                                        <div className="space-y-2">
                                          <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/60 backdrop-blur-sm border border-white/50">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                            <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-[0.15em]">
                                              {t('dashboard.all')}
                                            </span>
                                          </div>
                                          <p className="text-3xl font-light text-emerald-600 tracking-tight">
                                            {formatCount(state?.total_orders) || 0}
                                          </p>
                                          <p className="text-xs text-gray-400/60">Total orders</p>
                                        </div>
                                        
                                        <div className="flex-shrink-0 max-md:p-2 p-2.5 rounded-2xl bg-emerald-100 backdrop-blur-sm border border-white/50 shadow-[0_8px_32px_rgba(16,185,129,0.15)] transition-all duration-500 group-hover:scale-110 group-hover:-rotate-6">
                                          <Package className="h-7 w-7 max-md:w-5 max-md:h-5 text-emerald-500" />
                                        </div>
                                      </div>
                                      
                                      {/* Dynamic Progress */}
                                      <div className="mt-2 space-y-1.5">
                                        <div className="flex items-center justify-between">
                                          <span className="text-[10px] text-gray-400/60">Monthly progress</span>
                                          <span className="text-[10px] font-medium text-emerald-500">
                                            {(() => {
                                              const total = state?.total_orders || 0;
                                              const goal = 1000; 
                                              return `${Math.min(Math.round((total / goal) * 100), 100)}%`;
                                            })()}
                                          </span>
                                        </div>
                                        <div className="flex-1 h-1 rounded-full bg-white/50 backdrop-blur-sm overflow-hidden">
                                          <div 
                                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300 rounded-full transition-all duration-1000"
                                            style={{ 
                                              width: `${(() => {
                                                const total = state?.total_orders || 0;
                                                const goal = 500;
                                                return Math.min(Math.round((total / goal) * 100), 100);
                                              })()}%`
                                            }}
                                          >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Link>

                                {/* Status Cards with Dynamic Progress */}
                                {Object.entries(state?.orders_status || {}).map(([statusName, count], index) => {
                                  const totalOrders = state?.total_orders || 1; // Avoid division by zero
                                  const progressPercentage = Math.round((count / totalOrders) * 100);
                                  
                                  const statusConfig = {
                                    nouveau: { 
                                      color: 'bg-blue-300/40 via-blue-300/20 to-blue-50/60', 
                                      text: 'text-blue-600 dark:text-blue-400',
                                      bg: 'bg-blue-500',
                                      light: 'bg-blue-50/60 dark:bg-blue-200/20',
                                      icon: FilePlus2,
                                    },
                                    confirme: { 
                                      color: 'bg-emerald-300/40 via-emerald-300/30 to-emerald-50/60', 
                                      text: 'text-emerald-600 dark:text-emerald-400',
                                      bg: 'bg-emerald-500',
                                      light: 'bg-emerald-50/60 dark:bg-emerald-200/20',
                                      icon: CheckCircle,
                                    },
                                    suivi: { 
                                      color: 'bg-amber-300/40 via-amber-300/30 to-amber-50/60', 
                                      text: 'text-amber-600 dark:text-amber-400',
                                      bg: 'bg-amber-500',
                                      light: 'bg-amber-50/60 dark:bg-amber-200/20',
                                      icon: Headset,
                                    },
                                    reporte: { 
                                      color: 'bg-yellow-300/40 via-yellow-300/30 to-yellow-50/60', 
                                      text: 'text-yellow-600 dark:text-yellow-400',
                                      bg: 'bg-yellow-500',
                                      light: 'bg-yellow-50/60 dark:bg-yellow-200/20',
                                      icon: HelpCircle,
                                    },
                                    indelivery: { 
                                      color: 'bg-indigo-300/40 via-indigo-300/30 to-indigo-50/60', 
                                      text: 'text-indigo-600 dark:text-indigo-400',
                                      bg: 'bg-indigo-500',
                                      light: 'bg-indigo-50/60 dark:bg-indigo-200/20',
                                      icon: Truck,
                                    },
                                    livre: { 
                                      color: 'bg-emerald-300/40 via-emerald-300/30 to-emerald-50/60', 
                                      text: 'text-emerald-600 dark:text-emerald-400',
                                      bg: 'bg-emerald-500',
                                      light: 'bg-emerald-50/60 dark:bg-emerald-200/20',
                                      icon: PackageCheck,
                                    },
                                    retour: { 
                                      color: 'bg-orange-300/40 via-orange-300/30 to-orange-50/60', 
                                      text: 'text-orange-600 dark:text-orange-400',
                                      bg: 'bg-orange-500',
                                      light: 'bg-orange-50/60 dark:bg-orange-200/20',
                                      icon: Undo2,
                                    },
                                    refuse: { 
                                      color: 'bg-red-300/40 via-red-300/30 to-red-50/60', 
                                      text: 'text-red-600 dark:text-red-400',
                                      bg: 'bg-red-500',
                                      light: 'bg-red-50/60 dark:bg-red-200/20',
                                      icon: XCircle,
                                    },
                                    annule: { 
                                      color: 'bg-gray-300/40 via-gray-300/30 to-gray-50/60', 
                                      text: 'text-gray-600 dark:text-gray-400',
                                      bg: 'bg-gray-500',
                                      light: 'bg-gray-50/60 dark:bg-gray-200/20',
                                      icon: Ban,
                                    },
                                    duplicated: { 
                                      color: 'bg-pink-300/40 via-pink-300/30 to-pink-50/60', 
                                      text: 'text-pink-600 dark:text-pink-400',
                                      bg: 'bg-pink-500',
                                      light: 'bg-pink-50/60 dark:bg-pink-200/20',
                                      icon: Copy,
                                    }
                                  };

                                  const config = statusConfig[statusName] || statusConfig.nouveau;
                                  const IconComponent = config.icon;

                                  // Determine progress color based on percentage
                                  const getProgressColor = (percentage) => {
                                    if (percentage >= 70) return config.bg;
                                    if (percentage >= 40) return 'bg-amber-500';
                                    return 'bg-red-500';
                                  };

                                  return (
                                    <Link
                                      key={index}
                                      to={`/orders/${statusName}`}
                                      className={`group relative rounded-2xl overflow-hidden transition-all transition-all duration-800 hover:scale-[1.02] ${showContent ? "scale-100" : "scale-95"}`}
                                      style={{ animationDelay: `${index * 60}ms` }}
                                    >
                                      {/* Glass background with status color */}
                                      <div className={`absolute inset-0 bg-gradient-to-br ${config.color} rounded-2xl`} />
                                      <div className="absolute inset-0 backdrop-blur-xl bg-white/30 dark:bg dark:bg-stone-800/60 rounded-2xl" />
                                      <div className="absolute inset-0 rounded-2xl border border-white/50 dark:border-stone-700 group-hover:border-white/70 transition-colors duration-300" />
                                      
                                      {/* Decorative orb */}
                                      <div className={`absolute -top-16 -right-16 w-40 h-40 ${config.bg}/10 dark:bg-stone-800/50 rounded-full blur-3xl group-hover:opacity-70 transition-opacity duration-500`} />
                                      <div className={`absolute -bottom-16 -left-16 w-32 h-32 ${config.bg}/5 dark:bg-stone-800/30 rounded-full blur-2xl`} />
                                      
                                      {/* Animated gradient */}
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                      
                                      <div className="relative p-4 max-md:p-2.5">
                                        <div className="flex items-start justify-between">
                                          <div className="space-y-1.5 flex-1 min-w-0">
                                            {/* Status label */}
                                            <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${config.light} backdrop-blur-sm border border-white/40`}>
                                              <span className={`w-1 h-1 rounded-full ${config.bg} animate-pulse`} />
                                              <span className={`text-[9px] font-medium ${config.text} uppercase tracking-wider`}>
                                                {getStatusText(statusName, t)}
                                              </span>
                                            </div>
                                            
                                            {/* Count with status color */}
                                            <p className={`text-3xl font-light ${config.text} tracking-tight`}>
                                              {formatCount(count)}
                                            </p>
                                            
                                            {/* Percentage of total */}
                                            <p className="text-xs text-gray-400/60 dark:text-white/70">
                                              {progressPercentage}% of total orders
                                            </p>
                                          </div>
                                          
                                          {/* Icon with glass effect */}
                                          <div className={`flex-shrink-0 max-md:p-2 p-2.5 rounded-xl ${config.light} backdrop-blur-sm border border-white/40 transition-all duration-500 group-hover:scale-110 group-hover:-rotate-3 ml-3`}>
                                            <IconComponent className={`h-7 w-7 max-md:w-5 max-md:h-5 ${config.text}`} />
                                          </div>
                                        </div>
                                        
                                        {/* Progress bar with dynamic percentage */}
                                        <div className="mt-2 space-y-1.5">
                                          <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400/60 dark:text-white/70">Progress</span>
                                            <span className={`text-[10px] font-medium ${config.text}`}>
                                              {progressPercentage}%
                                            </span>
                                          </div>
                                          <div className="h-1 rounded-full bg-white/40 backdrop-blur-sm overflow-hidden">
                                            <div 
                                              className={`h-full ${getProgressColor(progressPercentage)} rounded-full transition-all duration-1000 relative`}
                                              style={{ 
                                                width: `${progressPercentage}%`,
                                                transitionDelay: `${index * 100}ms`
                                              }}
                                            >
                                              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                      </div>
                              </>
                              )}
                  </div>
                   </div>
              </main>
          </div>
      </div>
  );
}
